import {showNotification, Switch, SwitchProps} from 'platform/components';
import {Space} from 'platform/foundation';

import {isTrue} from 'ramda-adjunct';

import {useGetTenantSettingValuesQuery, useSetTenantSettingValueMutation} from '@dms/api/settings';
import featureFlags from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsStackItems, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {buildArray} from 'shared/web';

import {BranchPreselectionSection} from '../../components/BranchPreselectionSection/BranchPreselectionSection';

const OPEN_ON_CUSTOMER_CONTACT_SETTING_ID = 'interest-open_on_customer_contact';

export interface Items {
  title: string;
  url?: string;
  cyId?: string;
  switchProps?: SwitchProps;
}

export function Interest() {
  const {
    data: openOnCustomerContact,
    isLoading: isOpenOnCustomerContactLoading,
    isError: isOpenOnCustomerContactError,
  } = useGetTenantSettingValuesQuery({settingId: OPEN_ON_CUSTOMER_CONTACT_SETTING_ID});
  const [setTenantSettingValue, {isLoading: isOpenOnCustomerContactSaving}] =
    useSetTenantSettingValueMutation();

  const handleOpenOnCustomerContactChange = (value: boolean) =>
    setTenantSettingValue({
      settingId: OPEN_ON_CUSTOMER_CONTACT_SETTING_ID,
      valueRequestBody: {value, force: false},
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('general.notifications.changesSaved')))
      .catch(handleApiError);

  const items = buildArray<Items>()
    .add({
      cyId: testIds.settings.interestDefinition('link'),
      title: i18n.t('entity.seriesDefinition.labels.interestCodeDefinition'),
      url: settingsRoutes.interestDefinition,
    })
    .whenFeatureEnabled(featureFlags.CORE_TEAMS, {
      cyId: testIds.settings.salesLeadsAssignment('link'),
      title: i18n.t('entity.seriesDefinition.labels.salesLeadsAssignment'),
      url: settingsRoutes.salesLeadsAssignment,
    });

  return (
    <SettingsTemplate
      header={{title: i18n.t('entity.interest.labels.interest')}}
      data-testid="integrations-business-case-settings-modal"
    >
      <BranchPreselectionSection
        recordType="interest"
        label={i18n.t('page.salesSettings.branchPreselection.interestLabel')}
        data-testid={testIds.settings.interest('branchPreselection')}
      />
      <Switch
        label={i18n.t('page.salesSettings.openInterestOnCustomerContact.label')}
        helperText={i18n.t('page.salesSettings.openInterestOnCustomerContact.helperText')}
        value={isTrue(openOnCustomerContact?.[0]?.value)}
        onChange={handleOpenOnCustomerContactChange}
        // Saving without a loaded value would flip a switch the user never saw
        isDisabled={
          isOpenOnCustomerContactLoading ||
          isOpenOnCustomerContactError ||
          isOpenOnCustomerContactSaving
        }
        data-testid={testIds.settings.interest('openOnCustomerContact')}
      />
      <Space vertical={3} />
      <SettingsStackItems items={items} />
    </SettingsTemplate>
  );
}
